import clsx from "clsx";
import { forwardRef, useEffect, useRef, useState } from "react";
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, Eye, EyeOff } from "lucide-react";
import { useDismiss } from "@/hooks/useDismiss";

const CONTROL =
    "w-full rounded-xl border app-shell-border app-content-subtle app-text-primary px-3.5 py-2.5 text-sm " +
    "placeholder:text-[var(--app-text-soft)] focus:border-[var(--color-primary)] focus:outline-none " +
    "focus:ring-2 focus:ring-[var(--color-primary)]/25 transition-colors";

interface FieldProps {
    label?: string;
    hint?: string;
    error?: string;
    required?: boolean;
    children: ReactNode;
    className?: string;
}

export function Field({ label, hint, error, required, children, className }: FieldProps) {
    return (
        <div className={clsx("flex flex-col gap-1.5", className)}>
            {label && (
                <label className="app-text-primary text-sm font-medium">
                    {label}
                    {required && <span className="ml-0.5 text-[var(--color-error)]">*</span>}
                </label>
            )}
            {children}
            {error ? (
                <p className="text-xs text-[var(--color-error-text)]">{error}</p>
            ) : (
                hint && <p className="app-text-soft text-xs">{hint}</p>
            )}
        </div>
    );
}

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
    function Input({ className, ...rest }, ref) {
        return <input ref={ref} className={clsx(CONTROL, className)} {...rest} />;
    },
);

export const PasswordInput = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
    function PasswordInput({ className, ...rest }, ref) {
        const [visible, setVisible] = useState(false);
        const Icon = visible ? EyeOff : Eye;
        return (
            <div className="relative">
                <input
                    ref={ref}
                    type={visible ? "text" : "password"}
                    className={clsx(CONTROL, "pr-11", className)}
                    {...rest}
                />
                <button
                    type="button"
                    onClick={() => setVisible((v) => !v)}
                    aria-label={visible ? "Hide password" : "Show password"}
                    aria-pressed={visible}
                    className="app-text-soft absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-xl transition-colors hover:text-[var(--color-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)]/40"
                >
                    <Icon className="h-4.5 w-4.5" aria-hidden="true" />
                </button>
            </div>
        );
    },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(
    function Textarea({ className, ...rest }, ref) {
        return <textarea ref={ref} className={clsx(CONTROL, "min-h-24", className)} {...rest} />;
    },
);

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
    options: { value: string; label: string }[];
}

export function Select({ options, className, ...rest }: SelectProps) {
    return (
        <select className={clsx(CONTROL, "app-native-select pr-8", className)} {...rest}>
            {options.map((option) => (
                <option key={option.value} value={option.value}>
                    {option.label}
                </option>
            ))}
        </select>
    );
}

export interface MenuOption {
    value: string;
    label: string;
}

interface MenuSelectProps {
    /** Label aksesibilitas (aria-label) untuk tombol pemicu. */
    label: string;
    options: MenuOption[];
    value: string;
    onChange: (value: string) => void;
    className?: string;
}

/**
 * Dropdown custom pengganti `<select>` native.
 *
 * Popup `<select>` native di-render oleh browser/OS sehingga tidak bisa
 * di-style mengikuti tema (di dark mode muncul kotak hitam + highlight biru
 * bawaan — lihat menu /plan). `MenuSelect` memakai popup buatan sendiri
 * (`app-shell-panel`) sehingga nurut light/dark, dengan perilaku yang sama:
 * klik item memilih, klik luar / Escape menutup.
 */
export function MenuSelect({ label, options, value, onChange, className }: MenuSelectProps) {
    const [open, setOpen] = useState(false);
    const [anchor, setAnchor] = useState<{ top: number; left: number; width: number } | null>(
        null,
    );
    const triggerRef = useRef<HTMLSpanElement>(null);
    const ref = useDismiss<HTMLDivElement>(open, () => setOpen(false));

    const selected = options.find((option) => option.value === value) ?? options[0];

    // Popup di-portal ke body dengan posisi fixed supaya tidak kepotong
    // ancestor ber-overflow (mis. toolbar plan yang overflow-x-auto).
    // Posisi dihitung ulang tiap popup dibuka dan tiap scroll/resize.
    useEffect(() => {
        if (!open) return;

        const place = () => {
            const rect = triggerRef.current?.getBoundingClientRect();
            if (!rect) return;
            const width = Math.max(rect.width, 160);
            setAnchor({
                top: rect.bottom + 6,
                left: Math.min(rect.left, window.innerWidth - width - 8),
                width,
            });
        };

        place();
        window.addEventListener("scroll", place, true);
        window.addEventListener("resize", place);
        return () => {
            window.removeEventListener("scroll", place, true);
            window.removeEventListener("resize", place);
        };
    }, [open ]);

    return (
        <span ref={triggerRef} className={clsx("inline-flex shrink-0", className)}>
            <button
                type="button"
                aria-label={label}
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((prev) => !prev)}
                className="app-text-primary inline-flex w-full items-center gap-1.5 bg-transparent text-sm font-medium outline-none"
            >
                <span className="min-w-0 flex-1 truncate text-left">
                    {selected?.label ?? ""}
                </span>
                <ChevronDown
                    className={clsx(
                        "app-shell-text-muted h-4 w-4 shrink-0 transition-transform duration-200",
                        open && "rotate-180",
                    )}
                />
            </button>

            {open &&
                anchor &&
                createPortal(
                    <div ref={ref}>
                        <span
                            className="app-shell-panel app-shell-border fixed z-50 overflow-hidden rounded-xl border py-1 shadow-xl"
                            style={{ top: anchor.top, left: anchor.left, width: anchor.width }}
                        >
                            <ul role="listbox" aria-label={label}>
                                {options.map((option) => {
                                    const active = option.value === value;
                                    return (
                                        <li key={option.value}>
                                            <button
                                                type="button"
                                                role="option"
                                                aria-selected={active}
                                                onClick={() => {
                                                    onChange(option.value);
                                                    setOpen(false);
                                                }}
                                                className={clsx(
                                                    "flex w-full items-center gap-2 whitespace-nowrap px-3.5 py-2 text-left text-sm transition-colors",
                                                    active
                                                        ? "app-content-subtle app-text-primary font-semibold"
                                                        : "app-shell-text-muted hover:app-content-subtle-hover hover:text-[var(--app-text-primary)]",
                                                )}
                                            >
                                                <span className="min-w-0 flex-1 truncate">
                                                    {option.label}
                                                </span>
                                                {active && (
                                                    <Check className="h-4 w-4 shrink-0 text-emerald-500" />
                                                )}
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </span>
                    </div>,
                    document.body,
                )}
        </span>
    );
}
